/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import truncate from 'lodash/truncate';

import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';

// app: actions
import updateCollection from 'actions/user/updateCollection';

// app modules: props
import CollectionsProps from 'components/Collection/CollectionsProps';

// app modules: services
import route from 'services/route';

const generateShareText = collection => {
  const { description, name, display_name } = collection;
  let textToShare;

  if (name && display_name && description) {
    textToShare = `${name} - ${description} by ${display_name}`;
  } else if (name && description) {
    textToShare = `${name} - ${description}`;
  } else if (name && display_name) {
    textToShare = `${name} by ${display_name}`;
  } else {
    textToShare = name;
  }
  return textToShare;
};

function ShareMenuItem(props) {
  const {
    ItemComponent,
    Icon,
    value,
    label,
    secondary,
    collection,
    heading,
    onClick,
    onComplete,
    shareImageUrl,
    maxTextLength,
    ...others
  } = props;
  const { visibility } = collection;

  const baseUrl = useSelector(state => state.config.baseUrl);
  const dispatch = useDispatch();

  const handleClick = async () => {
    // If the collection is private we need to set it to unlisted first.
    // But we need to copy the link to the clipboard or call native share in the same frame as the click event to avoid the
    // browser blocking it (or prompting the user for permission).
    // So we will optimistically copy the unlisted URL of the collection to the clipboard and then update its permission.
    const url = `${baseUrl}${route.collection.view({
      ...collection,
      visibility: visibility === 'private' ? 'unlisted' : visibility,
    })}`;
    try {
      if (typeof onClick === 'function') {
        await onClick({
          title: collection.name || heading,
          url,
          text: truncate(generateShareText(collection), { length: maxTextLength, omission: '...' }),
          image: `${baseUrl}${shareImageUrl}`,
        });
      }
    } catch (err) {
      console.error('Failed to share: ', err);
      toast.error(`Failed to share`);
    }
    if (visibility === 'private') {
      // Set to unlisted
      await dispatch(updateCollection(collection.id, { visibility: 'unlisted' }));
      toast.success(
        <p>
          Collection privacy status is now <b>Unlisted</b>
        </p>,
        {
          autoClose: 5000,
        },
      );
    }
    setTimeout(onComplete, 1000);
  };
  let icon = null;
  if (React.isValidElement(Icon)) {
    icon = Icon;
  } else if (Icon) {
    icon = <Icon fontSize="large" color="primary" />;
  }
  return (
    <ItemComponent key={value} value={value} {...others} onClick={handleClick}>
      {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
      <ListItemText secondary={secondary}>{label}</ListItemText>
    </ItemComponent>
  );
}

ShareMenuItem.propTypes = {
  ItemComponent: PropTypes.elementType,
  Icon: PropTypes.elementType,
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  secondary: PropTypes.string,
  collection: CollectionsProps.collection.isRequired,
  heading: PropTypes.string,
  onClick: PropTypes.func,
  onComplete: PropTypes.func,
  shareImageUrl: PropTypes.string,
  maxTextLength: PropTypes.number,
};

ShareMenuItem.defaultProps = {
  ItemComponent: 'div',
  Icon: null,
  secondary: null,
  heading: null,
  onClick: null,
  onComplete: () => {},
  shareImageUrl: null,
  maxTextLength: 1000,
};

export default ShareMenuItem;
